import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import CardPriceSearch from './CardPriceSearch';
import { setToastsError, setToastsSuccess } from '../../../redux/actions/common';
import { useDispatch, useSelector } from 'react-redux';
import { ChangeRequestControllerService, ChangeRequestResult } from '../../../services';
import { setLoader } from '../../../redux/actions/api';
import ChangeRequestBase from './ChangeRequestBase';
import { getRequestingTable } from '../../tables/table';
import { discountValidation, validateFormInput } from '../../../utils/helpers/validation';
import { setDeleteConfirm, setIsContinue, setIsDeleteContinue } from '../../../redux/actions/modal';
import '../../../assets/css/pages/stores/change-request.css';
import '../../../assets/css/pages/stores/change-request-remand.css';
import apiErrorHandler, { apiValidationErrorHandler } from '../../../api/apiErrorHandler';
import { useLocation } from 'react-router-dom';
import { clearErrors } from '../../../redux/actions/error';
import ChomeModal from '../../modals/ChomeModal';
import OverLimitPriceModal from '../../modals/OverLimitPriceModal';
import { getSellingPrices } from '../../../utils/helpers/formula';
import { REQUEST_SELLING_PRICE_LIMIT } from '../../../utils/constants/threshold';

const ChangeRequest = (props) => {
    const { confirmStatus, confirmRequestId } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const storage = useSelector((state) => state.storage);
    const { isCompetitor, isFreshness, isInstruction, changeRequestType } = storage;

    const [state, setState] = useState({
        apiResponse: null,
        reload: new Date(),
        isShowModalEdit: false, // 編集モーダル
        editedId: '',
        isShowModalAllSelect: false,
        isShowForm: false,
        //非会員価格上限超えモーダル用State
        modalOverLimitPriceData: [],
        isShowOverLimitPriceModal: false,
        //超目モーダル用State
        modalChomeData: [],
        isShowChomeModal: false,
    });

    const [selectedData, setSelectedData] = useState([]);
    const [taxIncludedType, setTaxIncludedType] = useState('excluded');

    const {
        apiResponse,
        reload,
        isShowModalEdit,
        editedId,
        isShowModalAllSelect,
        isShowForm,
        modalOverLimitPriceData,
        isShowOverLimitPriceModal,
        modalChomeData,
        isShowChomeModal,
    } = state;

    const modal = useSelector((state) => state.modal);
    const { isContinue, isDeleteContinue } = modal;

    const isNearby = (isCompetitor && isShowForm) || confirmRequestId;
    const setApiResponse = (response) => {
        setState((prevState) => ({
            ...prevState,
            apiResponse: response,
        }));
    };

    const setIsShowForm = (bool) => {
        setState((prevState) => ({
            ...prevState,
            isShowForm: bool,
        }));
    };

    const [formData, setFormData] = useState(null);

    const handleEditClick = (index, value) => {
        setFormData(value);
        handleChangeState('isShowModalEdit', true);
    };

    const table = getRequestingTable(t, handleEditClick, confirmStatus === 'REQUESTING', isFreshness, isCompetitor);

    const handleChangeState = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleSubmitEditFormData = async (step = 0) => {
        dispatch(clearErrors());
        // 入力値チェック
        if (validateFormInput(dispatch, formData, isFreshness, isInstruction)) return;

        const discountedRequestSellingPrice = parseInt(formData.discountedRequestSellingPrice);
        const requestData = [
            {
                ...formData,
                discountedRequestSellingPrice,
            },
        ];

        const body = {
            discountedRequestSellingPrice: discountedRequestSellingPrice,
            salesStartDate: formData.salesStartDate,
            salesEndDate: formData.salesEndDate,
            otherSpecialReports: formData.otherSpecialReports,
        };

        try {
            await ChangeRequestControllerService.validateUpdateChangeRequest(formData.id, body);
        } catch (error) {
            apiValidationErrorHandler(dispatch, error, requestData);
            return;
        }

        // 編集後の非会員価格を取得して上限チェック
        const priceCheckedData = await getSellingPrices(dispatch, requestData);
        if (!priceCheckedData || checkOverLimitPrice(priceCheckedData)) {
            return;
        }

        // 乖離チェック
        if (await discountValidation(dispatch, priceCheckedData, isFreshness, true)) {
            return;
        }

        submitEdit();
    };

    useEffect(() => {
        dispatch(clearErrors());
        if (isContinue) {
            // 乖離モーダル表示後に処理を続行する場合
            submitEdit();
            dispatch(setIsContinue(false));
        }
        if (isDeleteContinue) {
            // 削除確認モーダル表示後に処理を続行する場合
            handleCheckDataDeleteSubmit();
            dispatch(setIsDeleteContinue(false));
        }
    }, [dispatch, isContinue, isDeleteContinue, location]);

    const submitEdit = () => {
        dispatch(setLoader(true));
        dispatch(clearErrors());
        const request = isCompetitor
            ? ChangeRequestControllerService.updateChangeRequestCompetitor(formData.id, formData)
            : isFreshness
              ? ChangeRequestControllerService.updateChangeRequestFreshness(formData.id, formData)
              : ChangeRequestControllerService.updateChangeRequestInstruction(formData.id, formData);

        request
            .then(() => {
                dispatch(setToastsSuccess('編集が完了しました。'));
                handleChangeState('isShowModalEdit', false);
                handleChangeState('reload', new Date());
                handleChangeState('editedId', formData.id);
            })
            .catch((error) => {
                apiValidationErrorHandler(dispatch, error, formData, t('message.error.update'));
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const handleCheckDataDeleteSubmit = () => {
        dispatch(clearErrors());
        const ids = selectedData.map((item) => item.id);
        const body = {
            changeRequestIds: ids,
            changeRequestType: changeRequestType,
        };
        ChangeRequestControllerService.deleteChangeRequest(body)
            .then(() => {
                dispatch(setToastsSuccess('削除が完了しました。'));
                handleChangeState('reload', new Date());
            })
            .catch((error) => {
                apiErrorHandler(dispatch, error, t('message.error.delete'));
            })
            .finally(() => {
                handleChangeState('isShowDeleteConfirmModal', false);
            });
    };

    // 非会員価格が上限以上の商品が含まれる場合はモーダルを表示して処理を中断する
    const checkOverLimitPrice = (items) => {
        const overLimitPriceData = items.filter((item) => item.requestSellingPrice >= REQUEST_SELLING_PRICE_LIMIT);
        if (overLimitPriceData.length) {
            setState((prevState) => ({
                ...prevState,
                modalOverLimitPriceData: overLimitPriceData,
                isShowOverLimitPriceModal: true,
            }));
            return true;
        }
        return false;
    };

    const handleShowSubmitModal = () => {
        if (checkOverLimitPrice(selectedData)) {
            return;
        }

        const chomeData = selectedData.filter((item) => item.lowestPriceType === ChangeRequestResult.lowestPriceType.CHOME);
        if (chomeData.length) {
            setState((prevState) => ({
                ...prevState,
                modalChomeData: chomeData,
                isShowChomeModal: true,
            }));
            return;
        }

        changeRequestValidateSubmit();
    };

    const changeRequestValidateSubmit = async () => {
        dispatch(clearErrors());
        dispatch(setLoader(true));

        if (selectedData.length) {
            const ids = selectedData.map((item) => {
                return {
                    changeRequestId: item.id,
                };
            });

            try {
                await ChangeRequestControllerService.validateUpdateChangeRequests({ data: ids });
            } catch (error) {
                const items = selectedData.filter((item) => error.body?.errors[0]?.data[0]?.jan === item.jan)[0];
                dispatch(setLoader(false));
                apiValidationErrorHandler(dispatch, error, [{ ...items }]);
                return;
            }

            const applyIds = selectedData.map((item) => item.id);
            ChangeRequestControllerService.apply({ changeRequestIds: applyIds })
                .then(() => {
                    dispatch(setToastsSuccess('申請しました。承認画面よりご確認ください。'));
                    handleChangeState('isShowModalApplication', false);
                    handleChangeState('reload', new Date());
                })
                .catch((error) => {
                    apiValidationErrorHandler(dispatch, error, selectedData, t('message.error.update'));
                })
                .finally(() => {
                    dispatch(setLoader(false));
                });
        } else {
            dispatch(setLoader(false));
            handleChangeState('isShowModalAllSelect', true);
        }
    };

    const handleShowDeleteModal = () => {
        showDeleteModal(selectedData);
    };

    const showDeleteModal = (selectedData) => {
        if (selectedData.length > 0) {
            dispatch(setDeleteConfirm(true));
        } else {
            dispatch(setToastsError('削除するデータを選択してください。'));
        }
    };

    const clearEditedId = () => {
        handleChangeState('editedId', '');
    };

    const handleChomeSubmit = () => {
        handleChangeState('isShowChomeModal', false);
        changeRequestValidateSubmit();
    };

    const renderBtn = () => (
        <div className="okc-pagination-bottom-btn c-slide-top mb-5">
            <Button
                id="submit-button"
                className="btn btn--primary mr-2"
                onClick={handleShowSubmitModal}
                disabled={apiResponse?.data.length === 0}
                children={<span className="btn-text">{t('price_request.apply_button')}</span>}
            />
            <Button
                id="delete-button"
                className="btn btn--primary"
                onClick={handleShowDeleteModal}
                disabled={apiResponse?.data.length === 0}
                children={<span className="btn-text">{t('price_request.delete_button')}</span>}
            />
        </div>
    );

    return (
        <>
            <ChangeRequestBase
                table={table}
                confirmStatus={confirmStatus}
                apiResponse={apiResponse}
                setApiResponse={setApiResponse}
                reload={reload}
                confirmRequestId={confirmRequestId}
                editedId={editedId}
                clearEditedId={clearEditedId}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
                btn={renderBtn}
                isShowForm={isShowForm}
                setIsShowForm={setIsShowForm}
                isNearby={isNearby}
            />
            <Modal
                isShow={isShowModalEdit}
                onClose={() => handleChangeState('isShowModalEdit', false)}
                isShowIconClose="true"
                title={isFreshness ? t('card_registration.freshness_title') : t('card_registration.title')}
                className="registration-edit-modal"
                btnList={[
                    {
                        name: t('button.update'),
                        className: 'btn--primary',
                        onClick: () => handleSubmitEditFormData(),
                        disabled: false,
                    },
                    {
                        name: t('button.cancel'),
                        className: 'btn--secondary',
                        onClick: () => handleChangeState('isShowModalEdit', false),
                        disabled: false,
                    },
                ]}
                children={
                    <CardPriceSearch
                        formData={formData}
                        setFormData={setFormData}
                        taxRate={formData?.taxRate}
                        capacityOfUnit={formData?.capacityOfUnit}
                        quantityOfUnit={formData?.quantityOfUnit}
                        discountedLowestSellingPrice={formData?.discountedLowestSellingPrice}
                        unit={formData?.unit}
                        taxIncludedType={taxIncludedType}
                        setTaxIncludedType={setTaxIncludedType}
                        isRegistration={false}
                        isNearby={isNearby}
                    />
                }
            />
            <Modal
                size="sm"
                isShow={isShowModalAllSelect}
                onClose={() => handleChangeState('isShowModalAllSelect', false)}
                isShowIconClose="true"
                title={t('message.info.please.select.data.to.request.title')}
                btnList={[
                    {
                        name: t('button.OK'),
                        className: 'btn--primary',
                        onClick: () => handleChangeState('isShowModalAllSelect', false),
                        disabled: false,
                    },
                ]}
                children={
                    <>
                        <p class="text-center">{t('message.info.please.select.data.to.request.request')}</p>
                    </>
                }
            />
            <OverLimitPriceModal
                isShow={isShowOverLimitPriceModal}
                onClose={() => handleChangeState('isShowOverLimitPriceModal', false)}
                modalData={modalOverLimitPriceData}
            />
            <ChomeModal
                isShow={isShowChomeModal}
                onClose={() => handleChangeState('isShowChomeModal', false)}
                onSubmit={handleChomeSubmit}
                modalData={modalChomeData}
                isFreshness={isFreshness}
            />
        </>
    );
};

export default ChangeRequest;
